import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Avatar from "@material-ui/core/Avatar";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import moment from "moment";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles(theme => ({
  paper: { padding: theme.spacing(2), borderRadius: 16, border: `1px solid ${theme.palette.divider}`, background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#fff" },
  topBar: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: theme.spacing(2), marginBottom: theme.spacing(2.5), flexWrap: "wrap" },
  title: { fontWeight: 800, fontSize: "1rem", color: theme.palette.type === "dark" ? "#fff" : "#0A1628" },
  filterField: { minWidth: 220 },
  formPaper: { padding: theme.spacing(2), borderRadius: 14, marginBottom: theme.spacing(2.5), border: `1px solid rgba(0,102,255,0.18)`, background: theme.palette.type === "dark" ? "rgba(0,102,255,0.06)" : "rgba(0,102,255,0.03)" },
  addBtn: { borderRadius: 12, fontWeight: 700, fontSize: "0.82rem", textTransform: "none", background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)", color: "#fff", "&:hover": { background: "linear-gradient(135deg, #0052CC 0%, #007ACC 100%)" } },
  blockRow: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), padding: theme.spacing(1.5), borderRadius: 12, border: `1px solid ${theme.palette.divider}`, marginBottom: theme.spacing(1), background: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "#fafafa" },
  blockAvatar: { width: 36, height: 36, background: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)", fontSize: "0.8rem", fontWeight: 700 },
  blockInfo: { flex: 1 },
  blockProf: { fontWeight: 700, fontSize: "0.85rem" },
  blockPeriod: { fontSize: "0.78rem", color: theme.palette.text.secondary },
  blockReason: { fontSize: "0.76rem", color: theme.palette.text.secondary, marginTop: 2 },
  emptyState: { textAlign: "center", padding: theme.spacing(5), color: theme.palette.text.secondary }
}));

const initialForm = { professionalId: "", startDate: moment().format("YYYY-MM-DD"), startTime: "00:00", endDate: moment().format("YYYY-MM-DD"), endTime: "23:59", reason: "" };

const ScheduleBlocksManager = () => {
  const classes = useStyles();
  const [professionals, setProfessionals] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [filterProfessional, setFilterProfessional] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingBlock, setDeletingBlock] = useState(null);

  const fetchProfessionals = useCallback(async () => {
    try {
      const { data } = await api.get("/users/list");
      setProfessionals(data || []);
    } catch (err) { toastError(err); }
  }, []);

  const fetchBlocks = useCallback(async professionalId => {
    try {
      const params = {};
      if (professionalId) params.professionalId = professionalId;
      const { data } = await api.get("/schedule-blocks", { params });
      setBlocks(data || []);
    } catch (err) { toastError(err); }
  }, []);

  useEffect(() => { fetchProfessionals(); }, [fetchProfessionals]);
  useEffect(() => { fetchBlocks(filterProfessional); }, [filterProfessional, fetchBlocks]);

  const handleChange = e => { setForm({ ...form, [e.target.name]: e.target.value }); };

  const handleOpenForm = () => {
    setForm({ ...initialForm, professionalId: filterProfessional || "" });
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setForm(initialForm);
  };

  const handleSave = async () => {
    if (!form.professionalId) {
      toast.error("Selecione um profissional");
      return;
    }
    const startTime = moment(`${form.startDate} ${form.startTime}`, "YYYY-MM-DD HH:mm");
    const endTime = moment(`${form.endDate} ${form.endTime}`, "YYYY-MM-DD HH:mm");
    if (!startTime.isValid() || !endTime.isValid() || startTime.isSameOrAfter(endTime)) {
      toast.error("Verifique as datas e horários informados");
      return;
    }
    try {
      await api.post("/schedule-blocks", {
        professionalId: form.professionalId,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        reason: form.reason
      });
      toast.success("Bloqueio criado");
      handleCloseForm();
      fetchBlocks(filterProfessional);
    } catch (err) { toastError(err); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/schedule-blocks/${deletingBlock.id}`);
      toast.success("Bloqueio removido");
    } catch (err) { toastError(err); }
    setDeletingBlock(null);
    fetchBlocks(filterProfessional);
  };

  const getProfName = id => { const p = professionals.find(p => p.id === id); return p ? p.name : "Profissional"; };
  const getInitials = name => name.split(" ").slice(0, 2).map(n => n[0]).join("").toUpperCase();

  return (
    <Paper className={classes.paper} variant="outlined">
      <ConfirmationModal title="Remover bloqueio?" open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete}>
        O profissional voltará a ficar disponível neste período.
      </ConfirmationModal>

      <div className={classes.topBar}>
        <div>
          <Typography className={classes.title}>Bloqueios de horário</Typography>
          <Typography variant="caption" color="textSecondary">Férias, feriados, ausências e folgas pontuais</Typography>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Filtrar por profissional</InputLabel>
            <Select value={filterProfessional} label="Filtrar por profissional" onChange={e => setFilterProfessional(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {professionals.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" className={classes.addBtn} startIcon={<AddIcon style={{ fontSize: 16 }} />} onClick={handleOpenForm}>
            Novo bloqueio
          </Button>
        </div>
      </div>

      {formOpen && (
        <Paper className={classes.formPaper} variant="outlined">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel>Profissional</InputLabel>
                <Select name="professionalId" value={form.professionalId} label="Profissional" onChange={handleChange}>
                  {professionals.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField label="Data início" name="startDate" type="date" value={form.startDate} onChange={handleChange} variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField label="Hora início" name="startTime" type="time" value={form.startTime} onChange={handleChange} variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField label="Data fim" name="endDate" type="date" value={form.endDate} onChange={handleChange} variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField label="Hora fim" name="endTime" type="time" value={form.endTime} onChange={handleChange} variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField label="Motivo (opcional)" name="reason" placeholder="Ex: Férias, feriado, consulta médica..." value={form.reason} onChange={handleChange} variant="outlined" size="small" fullWidth />
            </Grid>
            <Grid item xs={12} sm={3} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Button onClick={handleCloseForm} color="secondary" size="small">Cancelar</Button>
              <Button onClick={handleSave} color="primary" variant="contained" size="small">Salvar</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {blocks.length === 0 ? (
        <div className={classes.emptyState}>
          <EventBusyIcon style={{ fontSize: 36, marginBottom: 8, opacity: 0.4 }} />
          <Typography variant="body2">Nenhum bloqueio cadastrado.</Typography>
        </div>
      ) : (
        blocks.map(block => (
          <div className={classes.blockRow} key={block.id}>
            <Avatar className={classes.blockAvatar}>{getInitials(getProfName(block.professionalId))}</Avatar>
            <div className={classes.blockInfo}>
              <Typography className={classes.blockProf}>{getProfName(block.professionalId)}</Typography>
              <Typography className={classes.blockPeriod}>
                {moment(block.startTime).format("DD/MM/YYYY HH:mm")} até {moment(block.endTime).format("DD/MM/YYYY HH:mm")}
              </Typography>
              {block.reason && <Typography className={classes.blockReason}>{block.reason}</Typography>}
            </div>
            <Tooltip title="Remover bloqueio">
              <IconButton size="small" onClick={() => { setDeletingBlock(block); setConfirmOpen(true); }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ))
      )}
    </Paper>
  );
};

export default ScheduleBlocksManager;
